import React, { useState, useMemo } from "react";
import { Box, styled } from "@mui/material";
import { PerkQuirkHeader } from "./PerkQuirkHeader";
import { PerkQuirkCoverContent } from "./PerkQuirkCoverContent";
import { PerkQuirkCarousel } from "./PerkQuirkCarousel";
import { PerkQuirkCompletionContent } from "./PerkQuirkCompletionContent";
import { PERKS_QUIRKS_XP } from "../../../constants";
import type { PerkQuirkActivityProps } from "../../../types";

const PerkQuirkContainer = styled(Box, {
  label: "PerkQuirkContainer",
})(({ theme }) => ({
  position: "fixed",
  margin: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "#1c252e",
  zIndex: theme.zIndex.modal,
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
}));

const ContentArea = styled(Box, {
  label: "PerkQuirkContentArea",
})(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  maxWidth: "1280px",
  margin: "0 auto",
  width: "100%",
}));

interface PerkQuirkCombinedItem {
  id: string;
  name: string;
  description?: string;
  type: "perk" | "quirk";
}

const shuffleArrays = (
  perks: { id: string; name: string; description?: string }[],
  quirks: { id: string; name: string; description?: string }[],
): PerkQuirkCombinedItem[] => {
  const combined: PerkQuirkCombinedItem[] = [
    ...perks.map((item) => ({ ...item, type: "perk" as const })),
    ...quirks.map((item) => ({ ...item, type: "quirk" as const })),
  ];

  for (let i = combined.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [combined[i], combined[j]] = [combined[j], combined[i]];
  }

  return combined;
};

export const PerkQuirkActivity = ({
  onClose,
  hat,
  perkQuirkSentiments = [],
  onSentimentChange,
  activityCompletion,
  onComplete,
  onXPEarned,
}: PerkQuirkActivityProps) => {
  const xpValue = PERKS_QUIRKS_XP;
  const [currentStep, setCurrentStep] = useState(-1);

  const perkQuirkItems = useMemo(() => {
    const perks = hat.msPerks || [];
    const quirks = hat.msQuirks || [];
    return shuffleArrays(perks, quirks);
  }, [hat.msPerks, hat.msQuirks]);

  const completedItems = perkQuirkItems
    .filter((item) =>
      perkQuirkSentiments.find(
        (s) => String(s.perksQuirksId) === String(item.id),
      ),
    )
    .map((s) => s.id);

  const isLast = currentStep === perkQuirkItems.length - 1;
  const allItemsCompleted = perkQuirkItems.every((item) =>
    completedItems.includes(item.id),
  );
  const isLastAndCompleted = isLast && allItemsCompleted;

  const handleCompleted = () => {
    if (!activityCompletion) {
      onComplete?.();
      onXPEarned?.(xpValue, `perksquirks-${hat.id}`);
    }
  };

  const handleStart = () => {
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (currentStep < perkQuirkItems.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(perkQuirkItems.length);
      handleCompleted();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else if (currentStep === 0) {
      setCurrentStep(-1);
    }
  };

  const handleGoToItem = (index: number) => {
    setCurrentStep(index);
  };

  const handleContinue = () => {
    onClose();
  };

  const renderContent = () => {
    if (activityCompletion || currentStep === perkQuirkItems.length) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <PerkQuirkCompletionContent
            hat={hat}
            perkQuirkItems={perkQuirkItems}
            onContinue={handleContinue}
            xpValue={xpValue}
            sentiments={perkQuirkSentiments}
          />
        </Box>
      );
    } else if (currentStep === -1) {
      return (
        <>
          <PerkQuirkHeader
            onClose={onClose}
            progress={(currentStep / perkQuirkItems.length) * 100}
          />
          <ContentArea>
            <PerkQuirkCoverContent
              hat={hat}
              onStart={handleStart}
              xpValue={xpValue}
            />
          </ContentArea>
        </>
      );
    } else {
      return (
        <>
          <PerkQuirkHeader
            onClose={onClose}
            progress={
              ((currentStep + (isLastAndCompleted ? 1 : 0)) /
                perkQuirkItems.length) *
              100
            }
          />
          <ContentArea>
            <PerkQuirkCarousel
              items={perkQuirkItems}
              currentIndex={currentStep}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onGoToItem={handleGoToItem}
              completedItems={completedItems}
              sentiments={perkQuirkSentiments}
              onSentimentChange={onSentimentChange}
            />
          </ContentArea>
        </>
      );
    }
  };

  return <PerkQuirkContainer>{renderContent()}</PerkQuirkContainer>;
};
